import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { useTheme, Button, Badge } from '@mui/material';

// add icon
import AddIcon from '@mui/icons-material/Add';

import { green, red, blue, purple } from '@mui/material/colors';

import { ScrollableContainer, BasicFlex } from '../../utils/Common';

import KanbanTicket from './Components/KanbanTicket';

import SearchInput from '../../utils/utilities/SearchInput';

import SideDrawer from './Components/SideDrawer';

import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { searchTickets, getStations, getStationView } from './apiService';

// useSearchDebounce
import useSearchDebounce from '../../hooks/useSearchDebounce';

import StationsComponent from './Components/Stations';

import NoItems from '../../utils/NoItems';

import useServiceDashboardStore from '../../store/service_dashboard';

const mockStations = [
  { name: "Front Desk", id: 1, },
  { name: "Washing Area", id: 2, color: red }, 
  { name: "Cutting Station", id: 3, color: blue },
  { name: "Coloring Station", id: 4, color: purple },
  { name: "Prep Done", id: 5, color: green },
]

const mockTickets = [
  { service_ticket_id: 1, wig_id: "12345", service_name: "Wash & Set", client_name: "John Doe", status: "prepping", current_station: "Washing Area", checked_in_at: "2024-12-25T14:45:00.000+00:00", prep_stages: [{station: "Washing Area", received_at: "2024-12-25T14:45:00.000+00:00", completed_at: null, status: "pending"}, {station: "Cutting Station", received_at: null, completed_at: null, status: "pending"}] },
  {
    service_ticket_id: 2, wig_id: "MK5432", service_name: "Wash & Set", client_name: "John Doe", status: "prepping", current_station: "Cutting Station", checked_in_at: "2024-12-25T14:45:00.000+00:00", prep_stages: [{station: "Washing Area", received_at: "2024-12-25T14:45:00.000+00:00", completed_at: "2024-12-25T16:55:00.000+00:00", status: "completed"}, {station: "Cutting Station", received_at: "2024-12-26T10:55:00.000+00:00", completed_at: null, status: "pending"}]
  }
]

const Container = styled.div`
  overflow-y: hidden;
`

const TopBoard = styled(BasicFlex)`
  margin-bottom: 1rem;
  /* gap: 30rem; */

  @media ${props => props.theme.breaks.down('md')} {
    flex-direction: column;
    gap: .5rem;
    align-items: flex-start;
    margin-bottom: 2rem;
  }
`

const BoardTitle = styled.h2`
  margin: 0;
  margin-bottom: 1rem;
  font-size: 1.8rem;
  font-weight: 500;
  color: ${props => props.theme.colors.lightText};
  min-width: 20rem;
`

const NoItemsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50%;
  /* border: 2px dashed ${props => props.theme.colors.border}; */
  /* padding: 1.6rem; */
  /* border-radius: 8px; */
  /* background-color: ${props => props.theme.colors.lightBg}; */
` 

const KanbanContainer = styled.div`
    overflow-x: auto;
    overflow-y: hidden;

  @media ${props => props.theme.breaks.down('md')} {
    overflow-x: auto;
    width: 100%;
  }
`

const KanbanBoard = styled.div`
  display: flex;
  gap: 1.5rem;
`
const KanbanTrack = styled.div`
  min-width: 30rem;
  height: calc(100vh - 19rem);
  
`

const KanbanTrackHeader = styled(BasicFlex)`
 /* padding: 1rem; */
 margin-bottom: 1rem;
 background-color: ${props => props.$color ? props.$color : props.theme.colors.primaryFaint};
 border-radius: .8rem;
 padding: .6rem 1rem;
`

const KanbanTrackHeaderTitle = styled.div`
  /* font-size: 1.2rem; */
  font-weight: 600;
  /* color: ${props => props.theme.colors.lightText}; */
`

const KanbanTrackHeaderInfo = styled(BasicFlex)`
  gap: 1.6rem;
  min-width: fit-content;
  padding-left: .3rem;
`

const KanbanTrackHeaderActions = styled(BasicFlex)`
  gap: .8rem;
  justify-content: flex-end;
`

const KanbanDot = styled.div`
  width: .8rem;
  height: .8rem;
  border-radius: 50%;
  background-color: ${props => props.$color ? props.$color : props.theme.colors.primary};
`

const KanbanScrollable = styled(ScrollableContainer)`
  border-radius: 8px;
  padding: 1rem .5rem;
  padding-bottom: 2rem;
  background-color: ${props => props.theme.colors.darkBg};
  min-height: calc(100vh - 22rem);
  max-height: calc(100vh - 22rem);
`

let colors = [red, blue, purple, green];

const Dashboard = () => {
  const [sideBar, setSideBar] = useState({
    open: false,
    station: ""
  });
  const theme = useTheme();

  const sortedStations = useServiceDashboardStore((state) => state.sortedStations);
  const setSortedStations = useServiceDashboardStore((state) => state.setSortedStations);


  const handleClose = () => {
    setSideBar({ open: false, station: "" });
  }

  const handleOpen = (station) => {
    setSideBar({ open: true, station });
  }

  const { searchTerm, updateSearchTerm } = useSearchDebounce();

  const { data: tickets = [] } = useQuery({
    queryKey: ['service_tickets', searchTerm],
    queryFn: () => searchTickets(searchTerm),
    // enabled: !!searchTerm || searchTerm === "",
    keepPreviousData: true
  })

  const { data: stations } = useQuery({
    queryKey: ['stations'],
    queryFn: getStations,
    staleTime: 120 * 60 * 1000 // 2 hours
  })

  const { data: stationView } = useQuery({
    queryKey: ['settings', 'stations', 'station_view'],
    queryFn: getStationView,
    staleTime: 20 * 60 * 1000 // 2 hours
  })


  // set up stations
  useEffect(() => {
    // if (stationView?.data?.length) {
    //   // setSortedStations(stationView.data);
    // } else {
      let defaultStations = [{ id: 1, name: "Check In", active: true, filter: (sts) => {return sts.filter((st) => st.status === "checked_in")}, noReception: true }];

      if (stations?.length) {
        defaultStations = defaultStations.concat(stations.map((st, i) => ({ id: st.name, name: st.name, active: true, color: colors[i] })));
      }

      defaultStations = defaultStations.concat([{ id: defaultStations.length + 1, name: "Prep Done", active: true, filter: (sts) => {return sts.filter((st) => st.status === "prep_done")}, paletteColor: theme.palette.success.light}, { id: defaultStations.length + 2, name: "Appointment", active: true, filter: (sts) => {return sts.filter((st) => st.status === "in_progress")}, paletteColor: theme.palette.warning.light }]);

      if (stationView?.data?.length) {
        let newSort = {}
        defaultStations.forEach((st) => {
          let findIndex = stationView.data.findIndex((sv) => sv.name === st.name);
          if (findIndex !== -1) {
            newSort[findIndex] = {...st, active: stationView.data[findIndex].active};
          } else {

          }
        })
        defaultStations = Object.keys(newSort).sort((a, b) => a - b).map((key) => newSort[key]);
      }

      setSortedStations(defaultStations);
    // }
  }, [stations, stationView?.data]);

  return (
    <Container>
      <TopBoard>
        <BoardTitle>Station Dashboard</BoardTitle>
        <BasicFlex gap="1rem">
          <SearchInput onChange={updateSearchTerm} fieldsToSearch={[ "Customer", "Wig ID", "Ticket ID"]} />
          <StationsComponent />
        </BasicFlex>
      </TopBoard>

      <KanbanContainer>
        <KanbanBoard>
          {sortedStations.filter((station) => station.active).map((station) => {
            let filteredTickets = station && station.filter ? station.filter(tickets) : tickets.filter((ticket) => ticket.current_station === station.name);
            return (
            <KanbanTrack key={station.id}>
              <KanbanTrackHeader $color={station.paletteColor || station.color?.[100]}>
                <KanbanTrackHeaderInfo justify="flex-start">
                  {/* <KanbanDot $color={station.color} /> */}
                  <KanbanTrackHeaderTitle>{station.name}</KanbanTrackHeaderTitle>
                  <Badge badgeContent={filteredTickets.length} showZero={true} slotProps={{ badge: { sx: { bgcolor: "black", color: "white !important" } } }} />
                </KanbanTrackHeaderInfo>
                <KanbanTrackHeaderActions>
                  {/* { !station.noReception ? <Button size="small" sx={{p: 0, minWidth: "unset"}} color="text" onClick={() => handleOpen(station?.name)}><AddIcon  /></Button> : null } */}
                </KanbanTrackHeaderActions>
              </KanbanTrackHeader>
              <KanbanScrollable>
                {filteredTickets.length ? <BasicFlex dir="column" width="100%" gap="1rem">
                  {filteredTickets.map((ticket) => (
                    <KanbanTicket key={ticket.service_ticket_id} ticket={ticket} />
                  ))}
                </BasicFlex> : <NoItemsContainer><NoItems title="No Tickets" /></NoItemsContainer>}
              </KanbanScrollable>
            </KanbanTrack>
          )})}
        </KanbanBoard>
      </KanbanContainer>
      <SideDrawer sideBar={sideBar} handleClose={handleClose} />
    </Container>
  )
};

export default Dashboard;